import React, { useState, useEffect } from 'react';
import { UserPlus, Edit, Trash2, Mail, Phone, X, Save, ChevronLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';

const backendUrl = 'https://justurbane.in/backend_php/public';

const EmployeeManager = () => {
    const [employees, setEmployees] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const navigate = useNavigate();

    const [roles, setRoles] = useState([]); // Store dynamic roles here

    const [page, setPage] = useState(0);
    const perPage = 10;
    const paginated = employees.slice(page * perPage, (page + 1) * perPage);

    // Update initial form to use user_type 
    const initialForm = { 
        employee_id: '', 
        employee_name: '', 
        email: '', 
        emp_mobile: '', 
        emp_password: '', 
        type_id: '' // Changed from user_type
    };

    useEffect(() => {
        fetchRoles();   
        fetchEmployees();
    }, []);

    const fetchRoles = async () => {
        try {
            const res = await fetch(`${backendUrl}/api/admin/employee/getRoles`);
            const data = await res.json();
            
            // Debugging: See exactly what the backend is sending
            console.log("Roles API Response:", data);

            // If CI4 sends { "roles": [...] } or { "data": [...] }
            if (Array.isArray(data)) {
                setRoles(data);
            } else if (data.data && Array.isArray(data.data)) {
                setRoles(data.data);
            } else {
                console.error("API did not return an array", data);
                setRoles([]); // Fallback to empty array to prevent crash
            }
        } catch (err) {
            console.error("Fetch error:", err);
            setRoles([]); 
        }
    };
    // Form State
    const [formData, setFormData] = useState(initialForm);

    const fetchEmployees = async () => {
        setLoading(true);
        try {
            const res = await fetch(`${backendUrl}/api/admin/employee`);
            const data = await res.json();
            setEmployees(Array.isArray(data) ? data : []);
        } catch (err) { toast.error("Connection error"); }
        setLoading(false);
    };

    const handleOpenModal = (emp = null) => {
        if (emp) {
            setFormData({ 
                ...emp, 
                emp_password: '', 
                type_id: emp.type_id 
            });
        } else {
            setFormData(initialForm); // Reset to defaults for 'Add' mode
        }
        setIsModalOpen(true);
    };

    const handleSave = async (e) => {
        e.preventDefault();
        try {
            const res = await fetch(`${backendUrl}/api/admin/employee/save`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(formData)
            });
            if (res.ok) {
                toast.success(formData.employee_id ? "Updated" : "Added");
                setIsModalOpen(false);
                fetchEmployees();
            }
        } catch (err) { toast.error("Save failed"); }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Delete this employee?")) return;
        await fetch(`${backendUrl}/api/admin/employee/delete/${id}`, { method: 'DELETE' });
        fetchEmployees();
    };

    return (
        <div className="p-8 bg-gray-50 min-h-screen">
            <div className="max-w-7xl mx-auto">

                <header className="bg-white shadow-sm border-b">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center h-16">
                        <div className="flex items-center space-x-4">
                        <button
                            onClick={() => navigate('/admin/dashboard')}
                            className="flex items-center space-x-2 text-gray-600 hover:text-gray-900"
                        >
                            <ChevronLeft className="w-5 h-5" />
                            <span>Back to Dashboard</span>
                        </button>
                        <div className="h-6 w-px bg-gray-300"></div>
                        <h1 className="text-xl font-bold text-gray-900">Employees</h1>
                        </div>
                        
                        <button onClick={() => handleOpenModal()} className="bg-amber-500 text-white px-4 py-2 rounded-lg flex items-center gap-2">
                            <UserPlus size={18} /> Add Employee
                        </button>
                    </div>
                    </div>
                </header>
                
                {/* Table */}
                <div className="bg-white rounded-xl shadow border overflow-hidden">
                    <table className="w-full text-left">
                        <thead className="bg-gray-50 border-b">
                            <tr>
                                <th className="p-4">Sr.</th>
                                <th className="p-4">Name</th>
                                <th className="p-4">Contact</th>
                                <th className="p-4">Role</th>
                                <th className="p-4 text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            
                            {/* Change employees.map to paginated.map */}
                            {paginated.map((emp, i) => (
                                <tr key={emp.employee_id} className="border-b hover:bg-gray-50">
                                    {/* Correct Sr. No based on current page */}
                                    <td className="p-4">{(page * perPage) + i + 1}</td>
                                    
                                    <td className="p-4 font-medium">{emp.employee_name}</td>
                                    <td className="p-4 text-sm">
                                        <div className="flex items-center gap-1"><Phone size={12}/> {emp.emp_mobile}</div>
                                        <div className="flex items-center gap-1 text-gray-500"><Mail size={12}/> {emp.email}</div>
                                    </td>
                                    <td className="p-4"><span className="uppercase text-xs font-bold px-2 py-1 bg-gray-100 rounded">{emp.user_type}</span></td>
                                    <td className="p-4 text-right">
                                        <button onClick={() => handleOpenModal(emp)} className="p-2 text-blue-600"><Edit size={16}/></button>
                                        <button onClick={() => handleDelete(emp.employee_id)} className="p-2 text-red-600"><Trash2 size={16}/></button>
                                    </td>                                </tr>
                            ))}

                            {/* Simple Footer Navigation */}
                            <div className="flex justify-end items-center gap-4 p-4 bg-gray-50">
                                <button 
                                    disabled={page === 0}
                                    onClick={() => setPage(page - 1)}
                                    className="px-3 py-1 bg-white border rounded disabled:opacity-30"
                                >
                                    Prev
                                </button>
                                <span className="text-sm">Page {page + 1}</span>
                                <button 
                                    disabled={(page + 1) * perPage >= employees.length}
                                    onClick={() => setPage(page + 1)}
                                    className="px-3 py-1 bg-white border rounded disabled:opacity-30"
                                >
                                    Next
                                </button>
                            </div>
                        </tbody>
                    </table>
                </div>
            </div>

            {/* MODAL */}
            {isModalOpen && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl">
                        <div className="flex justify-between items-center p-6 border-b">
                            <h2 className="text-xl font-bold">{formData.employee_id ? 'Edit Employee' : 'Add Employee'}</h2>
                            <button onClick={() => setIsModalOpen(false)}><X /></button>
                        </div>
                        <form onSubmit={handleSave} className="p-6 space-y-4">
                            <div>
                                <label className="block text-sm font-medium mb-1">Full Name</label>
                                <input required type="text" className="w-full border rounded-lg p-2" value={formData.employee_name} onChange={e => setFormData({...formData, employee_name: e.target.value})} />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium mb-1">Email</label>
                                    <input 
                                        required 
                                        type="email" 
                                        placeholder="example@mail.com"
                                        className="w-full border rounded-lg p-2 focus:ring-2 focus:ring-amber-500 outline-none" 
                                        value={formData.email} 
                                        onChange={e => setFormData({...formData, email: e.target.value})} 
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-1">Mobile</label>
                                    <input 
                                        required 
                                        type="text" 
                                        maxLength="10"
                                        placeholder="10 digit number"
                                        className="w-full border rounded-lg p-2 focus:ring-2 focus:ring-amber-500 outline-none" 
                                        value={formData.emp_mobile} 
                                        // Only allow numbers to be typed
                                        onChange={e => {
                                            const val = e.target.value.replace(/\D/g, ''); // Remove non-digits
                                            setFormData({...formData, emp_mobile: val});
                                        }} 
                                    />
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">Password {formData.employee_id && "(Leave blank to keep)"}</label>
                                <input type="password" title="Set password" placeholder="••••••••" className="w-full border rounded-lg p-2" value={formData.emp_password} onChange={e => setFormData({...formData, emp_password: e.target.value})} />
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">Role</label>
                                <select 
                                    required
                                    className="w-full border rounded-lg p-2 bg-white" 
                                    // This must be type_id to match the role.id in the map below
                                    value={formData.type_id || ""} 
                                    onChange={e => setFormData({...formData, type_id: e.target.value})}
                                >
                                    <option value="" disabled>Select a Role</option>
                                    {Array.isArray(roles) && roles.map(role => (
                                        <option key={role.id} value={role.id}>
                                            {role.type_name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <button type="submit" className="w-full bg-amber-500 text-white py-3 rounded-lg font-bold flex items-center justify-center gap-2 hover:bg-amber-600">
                                <Save size={18}/> {formData.employee_id ? 'Update Employee' : 'Save Employee'}
                            </button>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default EmployeeManager;